/**
 * The one writer of `localStorage.authToken` after sign-in.
 *
 * WHY (Gymnastics of York, 2026-09-13): Stacy was signed out ~14 times in one
 * evening, "4 more times in 1 minute". Every sign-out window lined up with a
 * request storm from her session — 500–2,800 authenticated lookups per 5 minutes
 * against a normal ~33.
 *
 * The storm: the backend returns a freshly signed token on EVERY authenticated
 * response (X-Refreshed-Token, plus a `token` echoed in many bodies). A JWT's
 * `iat` is whole seconds, so two responses a second apart carry different
 * strings. The interceptor and ~40 screens wrote each one to localStorage, and
 * ~218 effects list `token` (read from localStorage on render) as a dependency
 * — so any response anywhere re-ran those fetches, whose responses changed the
 * token again. 197e132f3 fixed one such effect; this fixes the source.
 *
 * Rules:
 *  - Only a well-formed JWT is ever stored. A response body without `token`
 *    used to write the string "undefined" over a live session.
 *  - A refresh for the SAME user is not written while the stored token is less
 *    than MIN_TOKEN_AGE_BEFORE_REFRESH_MS old and not close to expiring. The
 *    session still slides (every 10 minutes instead of every response), and the
 *    token string — and every `[token]` effect — stays stable in between.
 *  - A refresh may only EXTEND the session already stored: the same person,
 *    issued AFTER the stored token, and not expired. A different user's token,
 *    an older one, an expired one, or any token when nothing is stored is
 *    refused. (2026-10-01, Gymnastics of York: the browser served cached API
 *    responses whose X-Refreshed-Token was the one from the day they were
 *    cached — often another person's on a shared computer. This used to write
 *    "a token for a different user" by rule, so it replaced the live session
 *    and the next session check signed the person out. The backend now marks
 *    those responses no-store; this guards the caches already out there.)
 *
 * Sign-in flows (Login, ResetPassword, AnonChatSigninField) start a NEW session
 * and write directly; everything else must come through here.
 */

import { safeGetItem, safeSetItem } from "@mediatechgroup/app-version";

export const AUTH_TOKEN_KEY = "authToken";
export const MIN_TOKEN_AGE_BEFORE_REFRESH_MS = 10 * 60 * 1000;

const JWT_SHAPE = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;

interface TokenClaims {
  sub?: string;
  authId?: string;
  userId?: string;
  iat?: number;
  exp?: number;
  /** Session start (epoch seconds), carried unchanged through every refresh. */
  st?: number;
}

/**
 * Who a token is for. Not every mint sets `sub`: the magic-link login issued
 * `authId`/`userId` only (2026-09-14). With no identity to compare, the
 * same-user check below was always false, so EVERY response wrote a new token
 * — the exact request storm the throttle exists to stop. Tokens live 7 days,
 * so a browser can still be holding one long after the mint was fixed.
 */
function identityOf(claims: TokenClaims | null): string | null {
  return claims?.sub || claims?.authId || claims?.userId || null;
}

/**
 * Who a stored token belongs to, for caching per-user data.
 *
 * The token STRING changes every time the session slides; the person does not.
 * Anything keyed on the string refetches on every slide, and on every remount.
 */
export function tokenIdentity(token: unknown): string | null {
  if (!isUsableToken(token)) return null;
  return identityOf(decodeClaims(token));
}

/**
 * Which sign-in a token belongs to: the session start (`st`, carried unchanged
 * through every refresh), or its `iat` for the sign-in token itself, which the
 * backend uses as `st` from then on. Null for anything that isn't a readable JWT.
 */
export function tokenSessionStart(token: unknown): number | null {
  if (!isUsableToken(token)) return null;
  const claims = decodeClaims(token);
  if (typeof claims?.st === "number") return claims.st;
  return typeof claims?.iat === "number" ? claims.iat : null;
}

/** When a token was issued (ISO), or null for anything that isn't a readable JWT. */
export function tokenIssuedAt(token: unknown): string | null {
  if (!isUsableToken(token)) return null;
  const iat = decodeClaims(token)?.iat;
  return typeof iat === "number" ? new Date(iat * 1000).toISOString() : null;
}

function decodeClaims(token: string): TokenClaims | null {
  try {
    const part = token.split(".")[1];
    const base64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const claims = JSON.parse(atob(padded));
    return claims && typeof claims === "object" ? (claims as TokenClaims) : null;
  } catch {
    return null;
  }
}

/**
 * True when the token's own `exp` says it has already expired. False for a
 * token with no `exp` or one that does not decode: those are not provably old.
 */
export function tokenExpired(token: unknown, now: number = Date.now()): boolean {
  if (!isUsableToken(token)) return false;
  const exp = decodeClaims(token)?.exp;
  return typeof exp === "number" && exp * 1000 <= now;
}

/** True only for a string shaped like a JWT whose payload decodes. */
export function isUsableToken(value: unknown): value is string {
  return typeof value === "string" && JWT_SHAPE.test(value) && decodeClaims(value) !== null;
}

function readStoredToken(): string | null {
  try {
    return safeGetItem(AUTH_TOKEN_KEY);
  } catch {
    return null;
  }
}

/**
 * Store a token handed back by the API. Returns true when storage changed.
 * `now` is injectable for tests.
 */
export function persistAuthToken(next: unknown, now: number = Date.now()): boolean {
  if (!isUsableToken(next)) return false;

  const current = readStoredToken();
  if (current === next) return false;

  // A refresh renews a session; it never starts one or switches whose it is.
  if (!isUsableToken(current)) return false;
  const cur = decodeClaims(current);
  const nxt = decodeClaims(next);
  const currentIdentity = identityOf(cur);
  if (!currentIdentity || currentIdentity !== identityOf(nxt)) return false;
  // Never step backwards (a cached copy of an older token of the same person),
  // and never store something already dead.
  if (typeof nxt?.iat !== "number" || typeof cur?.iat !== "number" || nxt.iat <= cur.iat) return false;
  if (typeof nxt?.exp === "number" && nxt.exp * 1000 <= now) return false;

  const issuedMs = cur.iat * 1000;
  const expiresMs = typeof cur?.exp === "number" ? cur.exp * 1000 : null;
  const stillFresh = now - issuedMs < MIN_TOKEN_AGE_BEFORE_REFRESH_MS;
  const notExpiringSoon = expiresMs === null || expiresMs - now > MIN_TOKEN_AGE_BEFORE_REFRESH_MS;
  if (stillFresh && notExpiringSoon) return false;

  return safeSetItem(AUTH_TOKEN_KEY, next);
}

/** The bearer token a request was sent with, from AxiosHeaders or a plain headers object. */
export function bearerToken(headers: unknown): string | null {
  if (!headers) return null;
  const h = headers as { get?: (name: string) => unknown; [key: string]: unknown };
  const raw =
    (typeof h.get === "function" ? h.get("Authorization") : undefined) ??
    h.Authorization ??
    h.authorization;
  if (typeof raw !== "string") return null;
  const match = /^Bearer\s+(.+)$/i.exec(raw.trim());
  return match ? match[1] : null;
}
